import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const NCAA_LOGO_BASE =
  'https://www.ncaa.com/sites/default/files/images/logos/schools';

const FAILURE_CACHE_TTL = 5 * 60 * 1000;

const TEAM_SLUG_ALIASES = {
  'purdue-fort-wayne': 'ipfw',
  'fort-wayne': 'ipfw',
  'texas-at-arlington': 'texas-arlington',
  'ut-arlington': 'texas-arlington',
  'uiw': 'incarnate-word',
  'appalachian-st': 'app-state',
  'appalachian-state': 'app-state',
  'georgia-southern': 'ga-southern',
  'louisiana-monroe': 'ulm',
  'la-monroe': 'ulm',
};

const successfulSourceCache = new Map();
const failedSourceCache = new Map();
// Offsets belong to a specific asset, not just a team. Light/dark logo files
// can use different SVG viewBoxes and must be measured independently.
const opticalOffsetCache = new Map();

function firstNonEmptyString(...values) {
  for (const value of values) {
    const normalized = String(value || '').trim();

    if (normalized) {
      return normalized;
    }
  }

  return '';
}

function getTeamName(team) {
  return firstNonEmptyString(
    team?.name,
    team?.nameShort,
    team?.nameFull,
    team?.displayName,
    team?.school,
    team?.names?.short,
    team?.names?.full,
    'Team'
  );
}

function getTeamInitials(name) {
  const words = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) {
    return 'VB';
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

function normalizeSlug(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\.svg$/i, '')
    .replace(/^#?\d+\s+/, '')
    .replace(/&/g, 'and')
    .replace(/[’']/g, '')
    .replace(/\./g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
}

function applySlugAlias(slug) {
  return TEAM_SLUG_ALIASES[slug] || slug;
}

function addUniqueValue(values, value) {
  const normalized = String(value || '').trim();

  if (!normalized || values.includes(normalized)) {
    return;
  }

  values.push(normalized);
}

function getSuppliedLogos(team) {
  const logos = [];

  [
    team?.logo,
    team?.logoUrl,
    team?.logoURL,
    team?.image,
    team?.imageUrl,
    team?.images?.logo,
    team?.links?.logo,
  ].forEach((logo) => addUniqueValue(logos, logo));

  return logos;
}

function getSlugFromLogoUrl(value) {
  const text = String(value || '').trim();

  if (!text) {
    return '';
  }

  try {
    const url = new URL(text, window.location.origin);
    const match = url.pathname.match(
      /\/(?:logo|bgl|bgd)\/([^/]+?)(?:\.svg)?$/i
    );

    return match
      ? normalizeSlug(decodeURIComponent(match[1]))
      : '';
  } catch {
    const match = text.match(
      /\/(?:logo|bgl|bgd)\/([^/?#]+?)(?:\.svg)?(?:[?#]|$)/i
    );

    return match ? normalizeSlug(match[1]) : '';
  }
}

function getSlugCandidates(team) {
  const candidates = [];
  const explicitValues = [
    team?.slug,
    team?.seoname,
    team?.seo,
    team?.teamSeo,
    team?.team_seo,
    team?.names?.seo,
    team?.names?.seoname,
  ];

  explicitValues.forEach((value) => {
    const rawSlug = normalizeSlug(value);
    addUniqueValue(candidates, applySlugAlias(rawSlug));
    addUniqueValue(candidates, rawSlug);
  });

  getSuppliedLogos(team).forEach((logo) => {
    const rawSlug = getSlugFromLogoUrl(logo);
    addUniqueValue(candidates, applySlugAlias(rawSlug));
    addUniqueValue(candidates, rawSlug);
  });

  return candidates.slice(0, 4);
}

function getTeamIdentityKey(team) {
  return firstNonEmptyString(
    normalizeSlug(team?.slug),
    normalizeSlug(team?.seoname),
    normalizeSlug(team?.seo),
    normalizeSlug(getTeamName(team)),
    'team'
  );
}

function isSameOriginLogoSource(value) {
  const source = String(value || '').trim();

  if (!source) {
    return false;
  }

  if (source.startsWith('/api/logo?')) {
    return true;
  }

  try {
    const url = new URL(source, window.location.origin);
    return (
      url.origin === window.location.origin &&
      url.pathname === '/api/logo'
    );
  } catch {
    return false;
  }
}

function hasTemporaryFailure(value) {
  const source = String(value || '').trim();

  if (!source || isSameOriginLogoSource(source)) {
    return false;
  }

  const failedAt = failedSourceCache.get(source);

  if (!Number.isFinite(failedAt)) {
    return false;
  }

  if (Date.now() - failedAt >= FAILURE_CACHE_TTL) {
    failedSourceCache.delete(source);
    return false;
  }

  return true;
}

function markTemporaryFailure(value) {
  const source = String(value || '').trim();

  if (!source || isSameOriginLogoSource(source)) {
    return;
  }

  failedSourceCache.set(source, Date.now());
}

function addUniqueSource(sources, url) {
  const normalized = String(url || '').trim();

  if (
    !normalized ||
    hasTemporaryFailure(normalized) ||
    sources.includes(normalized)
  ) {
    return;
  }

  sources.push(normalized);
}

function getLogoConfiguration(team, dark) {
  const identityKey = getTeamIdentityKey(team);
  const sources = [];

  addUniqueSource(sources, successfulSourceCache.get(identityKey));

  const slugs = getSlugCandidates(team);

  const firstSlug = slugs[0];

  if (firstSlug) {
    addUniqueSource(
      sources,
      `/api/logo?school=${encodeURIComponent(firstSlug)}${
        dark ? '&dark=1' : ''
      }`
    );
    const directory = dark ? 'bgd' : 'bgl';
    addUniqueSource(
      sources,
      `${NCAA_LOGO_BASE}/${directory}/${encodeURIComponent(
        firstSlug
      )}.svg`
    );
  }

  getSuppliedLogos(team).forEach((logo) => {
    addUniqueSource(sources, logo);
  });

  slugs.slice(1).forEach((slug) => {
    addUniqueSource(
      sources,
      `/api/logo?school=${encodeURIComponent(slug)}${dark ? '&dark=1' : ''}`
    );
  });

  return {
    identityKey,
    sources: sources.slice(0, 6),
  };
}

function imageHasVisiblePixels(image) {
  if (!image) {
    return false;
  }

  // A dispatched load event is sufficient in DOM implementations that do not
  // expose intrinsic image dimensions. Real browser failures use onError.
  if (image.naturalWidth < 1 || image.naturalHeight < 1) {
    return true;
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;

    const context = canvas.getContext('2d', {
      willReadFrequently: true,
    });

    if (!context) {
      return true;
    }

    context.clearRect(0, 0, 32, 32);
    context.drawImage(image, 0, 0, 32, 32);

    const pixels = context.getImageData(0, 0, 32, 32).data;
    let visiblePixels = 0;

    for (let index = 3; index < pixels.length; index += 4) {
      if (pixels[index] > 10) {
        visiblePixels += 1;

        if (visiblePixels >= 4) {
          return true;
        }
      }
    }

    return false;
  } catch {
    return true;
  }
}

function measureOpticalOffset(image) {
  if (!image?.naturalWidth || !image?.naturalHeight) return { x: 0, y: 0 };
  try {
    const size = 96;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return { x: 0, y: 0 };
    const scale = Math.min(size / image.naturalWidth, size / image.naturalHeight);
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    const left = (size - width) / 2;
    const top = (size - height) / 2;
    context.clearRect(0, 0, size, size);
    context.drawImage(image, left, top, width, height);
    const pixels = context.getImageData(0, 0, size, size).data;
    let minX = size;
    let minY = size;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        if (pixels[((y * size + x) * 4) + 3] <= 18) continue;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
    if (maxX < minX || maxY < minY) return { x: 0, y: 0 };
    const visibleCenterX = (minX + maxX) / 2;
    const visibleCenterY = (minY + maxY) / 2;
    const clamp = (value) => Math.max(-9, Math.min(9, value));
    return {
      x: clamp(((size / 2) - visibleCenterX) / size * 100),
      y: clamp(((size / 2) - visibleCenterY) / size * 100),
    };
  } catch {
    return { x: 0, y: 0 };
  }
}

export default function TeamLogo({
  team,
  variant = 'card',
  dark = false,
}) {
  const isMatchCenter = variant === 'match-center';
  const size = isMatchCenter
    ? 'clamp(58px, 7vw, 72px)'
    : '48px';
  const intrinsicSize = isMatchCenter ? 72 : 48;
  const teamName = getTeamName(team);
  const initials = getTeamInitials(teamName);

  const configuration = useMemo(
    () => getLogoConfiguration(team, dark),
    [team, dark]
  );

  const { identityKey, sources } = configuration;
  const sourceKey = sources.join('|');
  const identityRef = useRef(identityKey);

  const [displayedSource, setDisplayedSource] = useState(
    () => successfulSourceCache.get(identityKey) || ''
  );
  const [displayedReady, setDisplayedReady] = useState(
    () => Boolean(successfulSourceCache.get(identityKey))
  );
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [failed, setFailed] = useState(sources.length === 0);
  const [opticalOffset, setOpticalOffset] = useState(
    () => opticalOffsetCache.get(successfulSourceCache.get(identityKey)) || { x: 0, y: 0 }
  );

  useEffect(() => {
    const identityChanged = identityRef.current !== identityKey;
    const cachedSource = successfulSourceCache.get(identityKey) || '';

    identityRef.current = identityKey;
    setCandidateIndex(0);
    setOpticalOffset(opticalOffsetCache.get(cachedSource) || { x: 0, y: 0 });

    setDisplayedSource((current) => {
      if (identityChanged) {
        return cachedSource;
      }

      return current || cachedSource;
    });

    setDisplayedReady((current) => {
      if (identityChanged) {
        return Boolean(cachedSource);
      }

      return current || Boolean(cachedSource);
    });

    setFailed(
      sources.length === 0 &&
        !(identityChanged ? cachedSource : displayedSource || cachedSource)
    );
  }, [identityKey, sourceKey, sources.length]);

  const cachedSourceForIdentity =
    successfulSourceCache.get(identityKey) || '';
  const displayMatchesIdentity = identityRef.current === identityKey;
  const visibleDisplayedSource = displayMatchesIdentity
    ? displayedSource
    : cachedSourceForIdentity;
  const visibleDisplayedReady = displayMatchesIdentity
    ? displayedReady
    : Boolean(cachedSourceForIdentity);
  const candidateSource = sources[candidateIndex] || '';

  useEffect(() => {
    if (
      candidateSource &&
      visibleDisplayedSource &&
      candidateSource === visibleDisplayedSource
    ) {
      setCandidateIndex(sources.length);
      setFailed(false);
    }
  }, [candidateSource, visibleDisplayedSource, sources.length]);

  const fallbackClassName = isMatchCenter
    ? 'match-center-logo-fallback'
    : 'team-fallback';
  const imageClassName = isMatchCenter
    ? 'match-center-logo'
    : 'team-logo';

  function advanceCandidate() {
    if (candidateSource) {
      markTemporaryFailure(candidateSource);
    }

    const nextIndex = candidateIndex + 1;

    if (nextIndex < sources.length) {
      setCandidateIndex(nextIndex);
      setFailed(false);
      return;
    }

    setCandidateIndex(sources.length);
    setFailed(!visibleDisplayedSource);
  }

  function handleCandidateLoad(event) {
    const image = event.currentTarget;

    window.requestAnimationFrame(() => {
      if (!imageHasVisiblePixels(image)) {
        advanceCandidate();
        return;
      }

      successfulSourceCache.set(identityKey, candidateSource);
      image.style.opacity = '1';
      const nextOffset = measureOpticalOffset(image);
      opticalOffsetCache.set(candidateSource, nextOffset);
      setOpticalOffset(nextOffset);
      failedSourceCache.delete(candidateSource);
      setDisplayedSource(candidateSource);
      setDisplayedReady(true);
      setCandidateIndex(sources.length);
      setFailed(false);
    });
  }

  function handleDisplayedLoad(event) {
    if (visibleDisplayedSource) {
      successfulSourceCache.set(identityKey, visibleDisplayedSource);
      setDisplayedSource(visibleDisplayedSource);
    }

    setDisplayedReady(true);
    setFailed(false);
    const nextOffset = measureOpticalOffset(event.currentTarget);
    opticalOffsetCache.set(visibleDisplayedSource, nextOffset);
    setOpticalOffset(nextOffset);
  }

  function handleDisplayedError() {
    if (
      successfulSourceCache.get(identityKey) === visibleDisplayedSource
    ) {
      successfulSourceCache.delete(identityKey);
    }

    markTemporaryFailure(visibleDisplayedSource);
    setDisplayedSource('');
    setDisplayedReady(false);
    setCandidateIndex(0);
    setFailed(sources.length === 0);
  }

  return (
    <span
      className={`${fallbackClassName}${!visibleDisplayedReady && !failed ? ' loading' : ''}`}
      role="img"
      aria-label={
        failed
          ? `${teamName} logo unavailable`
          : `${teamName} logo`
      }
      style={{
        position: 'relative',
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        flex: `0 0 ${size}`,
        display: 'inline-grid',
        placeItems: 'center',
        overflow: 'hidden',
        boxSizing: 'border-box',
        isolation: 'isolate',
        lineHeight: 0,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          display: 'grid',
          placeItems: 'center',
          opacity: visibleDisplayedReady ? 0 : 1,
          transition: 'opacity 140ms ease',
        }}
      >
        {initials}
      </span>

      {visibleDisplayedSource ? (
        <img
          key={`displayed:${visibleDisplayedSource}`}
          className={imageClassName}
          src={visibleDisplayedSource}
          alt=""
          aria-hidden="true"
          loading={isMatchCenter ? 'eager' : 'lazy'}
          fetchPriority={isMatchCenter ? 'high' : 'low'}
          decoding="async"
          draggable="false"
          width={intrinsicSize}
          height={intrinsicSize}
          onLoad={handleDisplayedLoad}
          onError={handleDisplayedError}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'block',
            width: '100%',
            height: '100%',
            minWidth: '100%',
            minHeight: '100%',
            boxSizing: 'border-box',
            padding: isMatchCenter ? '7px' : '5px',
            objectFit: 'contain',
            objectPosition: 'center',
            transform: `translate(${opticalOffset.x}%, ${opticalOffset.y}%)`,
            transformOrigin: 'center',
            opacity: visibleDisplayedReady ? 1 : 0,
            pointerEvents: 'none',
            transition: 'opacity 140ms ease',
          }}
        />
      ) : null}

      {candidateSource && candidateSource !== visibleDisplayedSource ? (
        <img
          key={`candidate:${candidateSource}`}
          className={imageClassName}
          src={candidateSource}
          alt=""
          aria-hidden="true"
          loading={isMatchCenter ? 'eager' : 'lazy'}
          fetchPriority={isMatchCenter ? 'high' : 'low'}
          decoding="async"
          draggable="false"
          width={intrinsicSize}
          height={intrinsicSize}
          onLoad={handleCandidateLoad}
          onError={advanceCandidate}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </span>
  );
}
